// ─── AutoChinBot Monitor logic ─────────────────────────────────
// All state + business logic for the AutoChinBot Monitor, so the
// component stays a thin render layer.
//
// Phase 1 scope: connect to cTrader (price stream), remember the session,
// run in the background, and show the central control table
// (Symbol | Big Candle) plus a system-log feed that records
// every candle loaded into the edata array (and any load error).

import { useState, useEffect, useCallback, useRef, useMemo, startTransition } from 'react';
import { supabase } from '@/lib/supabase';
import type { Strategy } from '@/types/strategy';
import {
  ensureAutoChinTaskRuntime,
  getAutoChinTaskRuntime,
} from '@/lib/autochin-task-runtime';
import type { AutoChinEtaskConfig, AutoChinTaskEvent } from '@/lib/autochin-etask-engine';
import type { AutoChinRiskConfig } from '@/lib/autochin-risk-controller';
import { AUTOCHIN_MAXORDERS_EVENT } from '@/lib/autochin-order-engine';
import {
  AUTOCHIN_PENDING_PAUSE_EVENT,
  type AutoChinPendingOrder,
} from '@/lib/autochin-pending-controller';
import telegramBot from '@/lib/telegram-bot-client';
import {
  getOrCreateAutoChinEngine,
  getAutoChinEngine,
  type AutoChinBotSnapshot,
} from '@/lib/auto-chin-bot-engine';
import {
  useAutoChinWorkspacePersistence,
  type AutoChinWorkspaceData,
  type AutoChinSymbolConfig,
} from '@/hooks/useAutoChinWorkspacePersistence';
import {
  AUTO_CHIN_TIMEFRAME,
  type AutoChinCtraderAccount,
  type AutoChinSymbolList,
} from './auto-chin-monitor/types';

/** Map UI per-symbol config → etask engine config (only the task fields). */
function toEtaskConfigs(
  cfgs: Record<number, AutoChinSymbolConfig>,
): Record<number, AutoChinEtaskConfig> {
  const out: Record<number, AutoChinEtaskConfig> = {};
  Object.entries(cfgs).forEach(([k, v]) => {
    out[Number(k)] = { bigCandlePips: v?.bigCandlePips, st50Pips: v?.st50Pips };
  });
  return out;
}

/**
 * Map UI per-symbol config → task5 risk config (Rule 2 — maxOrders).
 * maxOrders has no UI column; it is delivered via the Telegram conversation
 * and persisted into workspace.symbolConfigs.
 */
function toRiskConfigs(
  cfgs: Record<number, AutoChinSymbolConfig>,
): Record<number, AutoChinRiskConfig> {
  const out: Record<number, AutoChinRiskConfig> = {};
  Object.entries(cfgs).forEach(([k, v]) => {
    if (v?.maxOrders != null) out[Number(k)] = { maxOrders: v.maxOrders };
  });
  return out;
}

interface UseAutoChinMonitorLogicArgs {
  strategy: Strategy;
  ctraderAccounts: AutoChinCtraderAccount[];
  isActive: boolean;
  initialAccountId?: string;
}

export function useAutoChinMonitorLogic({
  strategy,
  ctraderAccounts,
  isActive,
  initialAccountId,
}: UseAutoChinMonitorLogicArgs) {
  const strategyId = strategy.id || '';

  const [symbolLists, setSymbolLists] = useState<AutoChinSymbolList[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  const [selectedListId, setSelectedListId] = useState<string>('');
  const [selectedList, setSelectedList] = useState<AutoChinSymbolList | null>(null);

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [engineState, setEngineState] = useState<AutoChinBotSnapshot | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isEngineRunning, setIsEngineRunning] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Per-symbol "Trạng thái" value (free-form for now, designed later).
  const [statusMap, setStatusMap] = useState<Record<number, string>>({});
  // Per-symbol extra settings (functional columns will be added here later).
  const [symbolConfigs, setSymbolConfigs] = useState<Record<number, AutoChinSymbolConfig>>({});
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  // Recent task events (task1) emitted by the etask engine.
  const [taskEvents, setTaskEvents] = useState<AutoChinTaskEvent[]>([]);
  // Pending orders ("Vào lệnh chờ") shown in the Task Dashboard.
  const [pendingOrders, setPendingOrders] = useState<AutoChinPendingOrder[]>([]);
  const [pendingPaused, setPendingPaused] = useState(false);
  const [pendingPauseReason, setPendingPauseReason] = useState('');
  // Fallback user id — the detail page can pass user_id = ''.
  const [authUserId, setAuthUserId] = useState('');

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const startAttemptedRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  const effectiveAccountId = selectedAccountId;

  // ─── Workspace persistence ─────────────────────────────────

  const handleExternalWorkspace = useCallback((saved: AutoChinWorkspaceData) => {
    if (saved.selectedAccountId && saved.selectedAccountId !== selectedAccountId) {
      setSelectedAccountId(saved.selectedAccountId);
    }
    if (saved.statusMap) setStatusMap(saved.statusMap);
    if (saved.symbolConfigs) setSymbolConfigs(saved.symbolConfigs);
    if (typeof saved.pendingOrdersPaused === 'boolean') {
      setPendingPaused(saved.pendingOrdersPaused);
      pendingPausedRef.current = saved.pendingOrdersPaused;
    }
    if (saved.pendingOrdersPauseReason !== undefined) setPendingPauseReason(saved.pendingOrdersPauseReason || '');

    // Another device switched the symbol list — re-init the engine with the
    // new list's symbols so this device streams the correct market data too.
    if (saved.selectedListId && saved.selectedListId !== selectedListId) {
      const newList = symbolLists.find((l) => l.id === saved.selectedListId);
      if (newList) {
        setSelectedListId(newList.id);
        setSelectedList(newList);
        const symbolInfos = newList.symbols.map((s) => ({ symbolId: s.symbolId, symbolName: s.symbolName, digits: s.digits }));
        const accountToUse = saved.selectedAccountId || newList.ctrader_account_id;
        if (accountToUse) {
          const engine = getOrCreateAutoChinEngine(strategyId, accountToUse);
          engine.init(accountToUse, symbolInfos, AUTO_CHIN_TIMEFRAME);
        }
      }
    }
  }, [selectedAccountId, selectedListId, symbolLists, strategyId]);

  const {
    saveImmediate,
    loadWorkspace,
    cancelPendingSave,
    flushKeepalive,
    saveStatus,
  } = useAutoChinWorkspacePersistence(strategyId, effectiveAccountId, handleExternalWorkspace);

  // ─── Load symbol lists ─────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    async function loadLists() {
      setLoadingLists(true);
      try {
        const { data, error } = await supabase
          .from('symbol_lists')
          .select('id, name, ctrader_account_id, symbols')
          .order('name');

        if (error) throw error;
        if (!cancelled && data) setSymbolLists(data as AutoChinSymbolList[]);
      } catch (err) {
        console.warn('[AutoChinMonitor] Failed to load symbol lists:', err);
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    }
    loadLists();
    return () => { cancelled = true; };
  }, []);

  // ─── Restore workspace from Supabase ───────────────────────

  const lastRestoredAccountRef = useRef<string>('');

  useEffect(() => {
    if (loadingLists) return;
    // No symbol lists at all — stop showing the restoring spinner.
    if (symbolLists.length === 0) {
      setWorkspaceLoading(false);
      return;
    }
    if (lastRestoredAccountRef.current === effectiveAccountId) return;

    let cancelled = false;

    async function restore() {
      const saved = await loadWorkspace();
      if (cancelled) return;

      if (!saved || !saved.selectedListId) {
        lastRestoredAccountRef.current = effectiveAccountId;
        setWorkspaceLoading(false);
        return;
      }

      const savedList = symbolLists.find((l) => l.id === saved.selectedListId);
      if (!savedList) {
        lastRestoredAccountRef.current = effectiveAccountId;
        setWorkspaceLoading(false);
        return;
      }

      lastRestoredAccountRef.current = effectiveAccountId;

      setSelectedListId(saved.selectedListId);
      setSelectedList(savedList);
      if (saved.selectedAccountId) setSelectedAccountId(saved.selectedAccountId);
      if (saved.statusMap) setStatusMap(saved.statusMap);
      if (saved.symbolConfigs) setSymbolConfigs(saved.symbolConfigs);
      if (typeof saved.pendingOrdersPaused === 'boolean') setPendingPaused(saved.pendingOrdersPaused);
      if (saved.pendingOrdersPauseReason !== undefined) setPendingPauseReason(saved.pendingOrdersPauseReason || '');

      const symbolInfos = savedList.symbols.map((s) => ({ symbolId: s.symbolId, symbolName: s.symbolName, digits: s.digits }));
      const accountToUse = saved.selectedAccountId || savedList.ctrader_account_id;
      if (accountToUse) {
        const engine = getOrCreateAutoChinEngine(strategyId, accountToUse);
        engine.init(accountToUse, symbolInfos, AUTO_CHIN_TIMEFRAME);
      }

      setWorkspaceLoading(false);
    }

    restore();
    return () => { cancelled = true; };
  }, [loadingLists, symbolLists, strategyId, loadWorkspace, effectiveAccountId]);

  // ─── Auto-select account ───────────────────────────────────

  useEffect(() => {
    if (ctraderAccounts.length === 0) return;

    if (initialAccountId) {
      const match = ctraderAccounts.find((a) => String(a.account_id) === String(initialAccountId));
      if (match) { setSelectedAccountId(match.account_id); return; }
    }

    if (strategy.active_accounts && strategy.active_accounts.length > 0) {
      const match = ctraderAccounts.find((a) => String(a.account_id) === String(strategy.active_accounts[0]));
      if (match) { setSelectedAccountId(match.account_id); return; }
    }

    setSelectedAccountId(ctraderAccounts[0].account_id);
  }, [ctraderAccounts, strategy.active_accounts, initialAccountId]);

  const tradingAccountId = selectedAccountId || selectedList?.ctrader_account_id || '';

  // ─── Subscribe to engine ───────────────────────────────────

  useEffect(() => {
    if (!strategyId || !tradingAccountId) return;
    mountedRef.current = true;

    const engine = getOrCreateAutoChinEngine(strategyId, tradingAccountId);
    setIsEngineRunning(engine.isRunning);

    unsubscribeRef.current = engine.subscribe((s) => {
      if (!mountedRef.current) return;
      startTransition(() => {
        setEngineState(s);
        setIsEngineRunning(s.isRunning);
      });
    });

    setInitialized(true);

    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [strategyId, tradingAccountId]);

  // ─── Select a symbol list ──────────────────────────────────

  const symbolConfigsRef = useRef<Record<number, AutoChinSymbolConfig>>({});
  symbolConfigsRef.current = symbolConfigs;
  const statusMapRef = useRef<Record<number, string>>({});
  statusMapRef.current = statusMap;
  const pendingPausedRef = useRef(false);
  pendingPausedRef.current = pendingPaused;
  const pendingPauseReasonRef = useRef('');
  pendingPauseReasonRef.current = pendingPauseReason;

  const persist = useCallback((listId: string, statuses: Record<number, string>, accountId: string, configs?: Record<number, AutoChinSymbolConfig>) => {
    saveImmediate({
      selectedListId: listId,
      selectedAccountId: accountId,
      statusMap: statuses,
      // Preserve existing per-symbol settings unless explicitly replaced.
      symbolConfigs: configs ?? symbolConfigsRef.current,
      pendingOrdersPaused: pendingPausedRef.current,
      pendingOrdersPauseReason: pendingPauseReasonRef.current,
    });
  }, [saveImmediate]);

  const handleSelectList = useCallback((list: AutoChinSymbolList) => {
    setSelectedListId(list.id);
    setSelectedList(list);
    setDropdownOpen(false);
    setStatusMap({});

    const symbolInfos = list.symbols.map((s) => ({ symbolId: s.symbolId, symbolName: s.symbolName, digits: s.digits }));
    const accountToUse = selectedAccountId || list.ctrader_account_id;
    if (accountToUse) {
      const engine = getOrCreateAutoChinEngine(strategyId, accountToUse);
      engine.init(accountToUse, symbolInfos, AUTO_CHIN_TIMEFRAME);
    }

    persist(list.id, {}, selectedAccountId, {});
  }, [strategyId, selectedAccountId, persist]);

  // ─── Task runtime (edata + etask) ──────────────────────────
  // Ensure the task pipeline follows the same account as the market engine,
  // push the saved per-symbol configs into it, and mirror its events to the UI.
  const userId = strategy.user_id || authUserId;

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession()
      .then(({ data }) => { if (!cancelled) setAuthUserId(data.session?.user?.id || ''); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!strategyId || !tradingAccountId || !userId) return undefined;
    const rt = ensureAutoChinTaskRuntime(strategyId, tradingAccountId, userId);
    rt.applyConfigs(toEtaskConfigs(symbolConfigsRef.current));
    rt.applyRiskConfigs(toRiskConfigs(symbolConfigsRef.current));
    const unsub = rt.subscribe((evts) => {
      if (!mountedRef.current) return;
      startTransition(() => setTaskEvents(evts));
    });
    return () => { unsub(); };
  }, [strategyId, tradingAccountId, userId]);

  useEffect(() => {
    if (!strategyId || !tradingAccountId || !userId) return;
    const rt = getAutoChinTaskRuntime(strategyId, tradingAccountId);
    rt?.applyConfigs(toEtaskConfigs(symbolConfigs));
    rt?.applyRiskConfigs(toRiskConfigs(symbolConfigs));
  }, [symbolConfigs, strategyId, tradingAccountId, userId]);

  // task6 — when the order engine stores a maxOrders value delivered over
  // Telegram, merge it into this device's per-symbol config so a later save
  // can never drop it, and the live task5 risk gate picks it up.
  useEffect(() => {
    function onMaxOrders(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { strategyId?: string; accountId?: string; symbolId?: number; maxOrders?: number }
        | undefined;
      if (!detail || detail.symbolId == null || detail.maxOrders == null) return;
      if (detail.strategyId && detail.strategyId !== strategyId) return;
      if (detail.accountId && String(detail.accountId) !== String(effectiveAccountId)) return;
      setSymbolConfigs((prev) => {
        const cur = { ...(prev[detail.symbolId as number] || {}) };
        cur.maxOrders = detail.maxOrders as number;
        return { ...prev, [detail.symbolId as number]: cur };
      });
    }
    window.addEventListener(AUTOCHIN_MAXORDERS_EVENT, onMaxOrders);
    return () => window.removeEventListener(AUTOCHIN_MAXORDERS_EVENT, onMaxOrders);
  }, [strategyId, effectiveAccountId]);

  // ─── Per-symbol task config handler (Big Candle) ──────────
  const handleConfigChange = useCallback((
    symbolId: number,
    field: 'bigCandlePips' | 'st50Pips',
    value: number | null,
  ) => {
    setSymbolConfigs((prev) => {
      const nextCfg = { ...(prev[symbolId] || {}) };
      if (value === null || isNaN(value)) delete nextCfg[field];
      else nextCfg[field] = value;
      const next = { ...prev, [symbolId]: nextCfg };
      persist(selectedListId, statusMapRef.current, selectedAccountId, next);
      return next;
    });
  }, [selectedListId, selectedAccountId, persist]);

  // Latest task status text per symbol (newest event wins).
  const taskStatusMap = useMemo(() => {
    const m: Record<number, string> = {};
    for (const e of taskEvents) {
      if (!m[e.symbolId]) m[e.symbolId] = e.message;
    }
    return m;
  }, [taskEvents]);

  // ─── Start engine ──────────────────────────────────────────

  const startEngine = useCallback(async () => {
    const acctToUse = selectedAccountId || selectedList?.ctrader_account_id;
    if (!acctToUse || !strategyId || !selectedList) return;

    const engine = getOrCreateAutoChinEngine(strategyId, acctToUse);
    if (engine.isRunning) return;

    const symbolInfos = selectedList.symbols.map((s) => ({ symbolId: s.symbolId, symbolName: s.symbolName, digits: s.digits }));
    await engine.init(acctToUse, symbolInfos, AUTO_CHIN_TIMEFRAME);
    engine.start();
  }, [selectedAccountId, selectedList, strategyId]);

  const stopEngine = useCallback(() => {
    if (!strategyId || !tradingAccountId) return;
    const engine = getAutoChinEngine(strategyId, tradingAccountId);
    if (engine) engine.stop();
  }, [strategyId, tradingAccountId]);

  // Auto-start when the strategy is active for this account.
  useEffect(() => {
    if (selectedList && selectedAccountId && initialized && isActive && !startAttemptedRef.current) {
      startAttemptedRef.current = true;
      startEngine();
    }
  }, [selectedList, selectedAccountId, initialized, isActive, startEngine]);

  useEffect(() => {
    startAttemptedRef.current = false;
  }, [selectedAccountId, selectedListId]);

  // ─── Status column handler ─────────────────────────────────

  const handleStatusChange = useCallback((symbolId: number, value: string) => {
    setStatusMap((prev) => {
      const next = { ...prev, [symbolId]: value };
      persist(selectedListId, next, selectedAccountId);
      return next;
    });
  }, [selectedListId, selectedAccountId, persist]);

  // ─── Close dropdown on outside click ───────────────────────

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ─── beforeunload flush ────────────────────────────────────

  useEffect(() => {
    const handler = () => {
      cancelPendingSave();
      flushKeepalive();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [flushKeepalive, cancelPendingSave]);

  useEffect(() => () => { cancelPendingSave(); }, [cancelPendingSave]);

  // ─── Pending orders ("Vào lệnh chờ") ────────────────────────

  const refreshPendingOrders = useCallback(async () => {
    if (!tradingAccountId) return;
    try {
      const { data } = await supabase
        .from('telegram_order_proposals')
        .select('id, chat_id, telegram_account_id, account_id, symbol_id, symbol, trade_side, volume, status, signal_context, expires_at, error_message, created_at')
        .eq('action', 'pending_order')
        .eq('account_id', tradingAccountId)
        .in('status', ['pending_new', 'pending_armed'])
        .order('created_at', { ascending: false });
      if (data) setPendingOrders(data as AutoChinPendingOrder[]);
    } catch {
      /* ignore — retry on the next poll */
    }
  }, [tradingAccountId]);

  // Poll the pending list (and keep it fresh after any engine action).
  useEffect(() => {
    if (!tradingAccountId) return undefined;
    refreshPendingOrders();
    const t = setInterval(() => { void refreshPendingOrders(); }, 5000);
    return () => clearInterval(t);
  }, [tradingAccountId, refreshPendingOrders]);

  // The pause state is owned by the pending engine (persisted in the
  // workspace); mirror it here so the dashboard can show + toggle it.
  useEffect(() => {
    function onPause(e: Event) {
      const d = (e as CustomEvent).detail as
        | { strategyId?: string; accountId?: string; paused?: boolean; reason?: string }
        | undefined;
      if (!d) return;
      if (d.strategyId && d.strategyId !== strategyId) return;
      if (d.accountId && String(d.accountId) !== String(effectiveAccountId)) return;
      setPendingPaused(!!d.paused);
      setPendingPauseReason(d.reason || '');
      void refreshPendingOrders();
    }
    window.addEventListener(AUTOCHIN_PENDING_PAUSE_EVENT, onPause);
    return () => window.removeEventListener(AUTOCHIN_PENDING_PAUSE_EVENT, onPause);
  }, [strategyId, effectiveAccountId, refreshPendingOrders]);

  const deletePendingOrder = useCallback(async (id: string) => {
    try {
      await telegramBot.cancelPendingOrder(id, 'Người dùng xoá từ Task Dashboard');
    } catch {
      /* best-effort */
    }
    void refreshPendingOrders();
  }, [refreshPendingOrders]);

  // Edit a pending order (price / volume / validity) from the dashboard.
  // The row is reset to pending_new server-side, so the background engine
  // re-validates the new price against the live market before re-arming it.
  const updatePendingOrder = useCallback(async (
    id: string,
    patch: { price: number; volume: number; hours: number },
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await telegramBot.updatePendingOrder(id, patch);
      if (!res.success) return { success: false, error: res.error || 'Không cập nhật được lệnh chờ.' };
      if (strategyId && tradingAccountId) {
        getAutoChinTaskRuntime(strategyId, tradingAccountId)?.pending.notifyEdited(id);
      }
      void refreshPendingOrders();
      return { success: true };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }, [strategyId, tradingAccountId, refreshPendingOrders]);

  const resumePendingOrders = useCallback(async () => {
    if (!strategyId || !tradingAccountId) return;
    const rt = getAutoChinTaskRuntime(strategyId, tradingAccountId);
    await rt?.pending.resume();
    setPendingPaused(false);
    setPendingPauseReason('');
  }, [strategyId, tradingAccountId]);

  // ─── Derived ───────────────────────────────────────────────

  const connected = engineState?.connected ?? false;
  const isLoading = engineState?.loading ?? false;
  const errorMsg = engineState?.lastError || '';
  const currentAccount = ctraderAccounts.find((a) => String(a.account_id) === String(selectedAccountId));
  const displaySymbols = selectedList ? selectedList.symbols : [];

  return {
    symbolLists,
    loadingLists,
    selectedListId,
    selectedList,
    dropdownOpen,
    setDropdownOpen,
    dropdownRef,
    selectedAccountId,
    engineState,
    isEngineRunning,
    statusMap,
    symbolConfigs,
    taskStatusMap,
    pendingOrders,
    pendingPaused,
    pendingPauseReason,
    workspaceLoading,
    saveStatus,
    connected,
    isLoading,
    errorMsg,
    currentAccount,
    displaySymbols,
    handleSelectList,
    startEngine,
    stopEngine,
    handleStatusChange,
    handleConfigChange,
    deletePendingOrder,
    updatePendingOrder,
    resumePendingOrders,
  };
}